/**
 * Castle procedural textures — painted onto canvases at boot, no assets.
 * Every painter returns an RGBA CanvasTexture where RGB is albedo (sRGB) and
 * ALPHA packs a height field the triplanar material turns into bump relief.
 */
import * as THREE from 'three';

export function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function makeCanvas(size: number): { canvas: HTMLCanvasElement; ctx: CanvasRenderingContext2D } {
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('2d context unavailable');
  return { canvas, ctx };
}

function toTexture(canvas: HTMLCanvasElement): THREE.CanvasTexture {
  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.wrapS = THREE.RepeatWrapping;
  tex.wrapT = THREE.RepeatWrapping;
  tex.magFilter = THREE.LinearFilter;
  tex.minFilter = THREE.LinearMipmapLinearFilter;
  tex.generateMipmaps = true;
  tex.anisotropy = 8;
  return tex;
}

/** Multiply albedo by low-frequency weathering blotches; keep height intact. */
function weather(ctx: CanvasRenderingContext2D, size: number, rng: () => number, strength: number, count: number): void {
  ctx.globalCompositeOperation = 'source-atop';
  ctx.globalAlpha = strength;
  for (let i = 0; i < count; i += 1) {
    const x = rng() * size;
    const y = rng() * size;
    const r = (0.04 + rng() * 0.16) * size;
    const g = ctx.createRadialGradient(x, y, r * 0.15, x, y, r);
    const dark = rng() > 0.4;
    g.addColorStop(0, dark ? 'rgba(52,44,34,0.55)' : 'rgba(238,230,210,0.4)');
    g.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = g;
    ctx.fillRect(x - r, y - r, r * 2, r * 2);
  }
  ctx.globalAlpha = 1;
  ctx.globalCompositeOperation = 'source-over';
}

/** Per-pixel speckle on albedo only. */
function speckle(data: Uint8ClampedArray, size: number, rng: () => number, amount: number): void {
  for (let i = 0; i < size * size; i += 1) {
    const n = (rng() - 0.5) * amount;
    const j = i * 4;
    data[j] += n;
    data[j + 1] += n;
    data[j + 2] += n;
  }
}

export interface PaintedTexture {
  albedo: THREE.CanvasTexture;
}

/**
 * Coursed limestone ashlar. Tile covers ~2.8 m of wall: 7 stones per course,
 * ~13 courses, mortar gaps dark and LOW in the alpha-height channel.
 */
export function paintAshlar(seed = 7): PaintedTexture {
  const size = 1024;
  const { canvas, ctx } = makeCanvas(size);
  const rng = mulberry32(seed);

  // Mortar bed.
  ctx.fillStyle = '#574f42';
  ctx.fillRect(0, 0, size, size);

  const rows = 13;
  const rowH = size / rows;
  const img = ctx.getImageData(0, 0, size, size);
  const data = img.data;
  const setPx = (x: number, y: number, r: number, g: number, b: number, a: number) => {
    const xi = ((x % size) + size) % size;
    const yi = ((y % size) + size) % size;
    const j = (yi * size + xi) * 4;
    data[j] = r;
    data[j + 1] = g;
    data[j + 2] = b;
    data[j + 3] = a;
  };

  for (let row = 0; row < rows; row += 1) {
    const y0 = Math.round(row * rowH);
    const y1 = Math.round((row + 1) * rowH) - 2; // 2px mortar joint
    let x = -Math.floor(rng() * 90);
    while (x < size) {
      const w = Math.round(rowH * (1.9 + rng() * 1.9));
      const xEnd = Math.min(x + w, size + 60);
      // Stone body colour: warm limestone with occasional ochre outlier.
      const ochre = rng() < 0.09;
      const baseR = ochre ? 176 : 205 + (rng() - 0.5) * 26;
      const baseG = ochre ? 150 : 192 + (rng() - 0.5) * 26;
      const baseB = ochre ? 116 : 164 + (rng() - 0.5) * 24;
      const dome = 0.82 + rng() * 0.16;
      for (let py = y0; py <= y1 && py < size; py += 1) {
        const ty = (py - y0) / Math.max(1, y1 - y0);
        for (let px = Math.max(0, x); px < xEnd && px < size; px += 1) {
          const tx = (px - x) / Math.max(1, w);
          // Bevel: brighten crown, darken skirt; drop into mortar at edges.
          const edge = Math.min(tx, 1 - tx, ty, 1 - ty);
          const bev = Math.min(1, edge / 0.2);
          let r = baseR * (0.86 + dome * 0.22 * Math.sin(Math.PI * ty));
          let g = baseG * (0.86 + dome * 0.22 * Math.sin(Math.PI * ty));
          let b = baseB * (0.86 + dome * 0.22 * Math.sin(Math.PI * ty));
          r *= 0.92 + bev * 0.14;
          g *= 0.92 + bev * 0.14;
          b *= 0.92 + bev * 0.14;
          const h =
            edge < 0.05 ? 40 + edge * 20 * 20 : (48 + dome * 190) * (0.75 + bev * 0.25);
          setPx(px, py, r, g, b, h);
        }
      }
      x = xEnd;
    }
  }

  speckle(data, size, rng, 14);
  ctx.putImageData(img, 0, 0);
  weather(ctx, size, rng, 0.5, 46);
  return { albedo: toTexture(canvas) };
}

/** Weathered limewash plaster for the keep upper storeys. */
export function paintLimewash(seed = 11): PaintedTexture {
  const size = 512;
  const { canvas, ctx } = makeCanvas(size);
  const rng = mulberry32(seed);
  ctx.fillStyle = '#ddd5c2';
  ctx.fillRect(0, 0, size, size);
  // Cloudy mottling.
  for (let i = 0; i < 260; i += 1) {
    const x = rng() * size;
    const y = rng() * size;
    const r = (0.02 + rng() * 0.09) * size;
    const g = ctx.createRadialGradient(x, y, 1, x, y, r);
    const dark = rng() > 0.45;
    g.addColorStop(0, dark ? 'rgba(120,110,92,0.16)' : 'rgba(255,252,240,0.2)');
    g.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = g;
    ctx.fillRect(x - r, y - r, r * 2, r * 2);
  }
  // Rain streaks from the top.
  for (let i = 0; i < 40; i += 1) {
    const x = rng() * size;
    const w = 2 + rng() * 7;
    const len = (0.2 + rng() * 0.7) * size;
    const g = ctx.createLinearGradient(0, 0, 0, len);
    g.addColorStop(0, 'rgba(96,88,74,0.22)');
    g.addColorStop(1, 'rgba(96,88,74,0)');
    ctx.fillStyle = g;
    ctx.save();
    ctx.translate(x, 0);
    ctx.fillRect(0, 0, w, len);
    ctx.restore();
  }
  const img = ctx.getImageData(0, 0, size, size);
  speckle(img.data, size, rng, 10);
  // Height: mostly flat with cloudy relief.
  for (let i = 0; i < size * size; i += 1) img.data[i * 4 + 3] = 208 + (rng() - 0.5) * 22;
  ctx.putImageData(img, 0, 0);
  return { albedo: toTexture(canvas) };
}

/** Slate-shingle courses for tower roofs. Tile ~2 m. */
export function paintShingle(seed = 19): PaintedTexture {
  const size = 512;
  const { canvas, ctx } = makeCanvas(size);
  const rng = mulberry32(seed);
  ctx.fillStyle = '#2c2f33';
  ctx.fillRect(0, 0, size, size);
  const rows = 10;
  const rowH = size / rows;
  for (let row = 0; row < rows; row += 1) {
    const y0 = row * rowH;
    const offset = row % 2 === 0 ? 0 : rowH * 0.75;
    for (let x = -rowH * 2; x < size; x += rowH * 1.5) {
      const sx = x + offset;
      const w = rowH * 1.5 - 2;
      const shade = 0.78 + rng() * 0.4;
      const hue = 96 + (rng() - 0.5) * 14;
      ctx.fillStyle = `rgb(${Math.round(hue * shade)},${Math.round((hue + 4) * shade)},${Math.round((hue + 9) * shade)})`;
      ctx.fillRect(sx, y0, w, rowH - 2);
      ctx.fillStyle = 'rgba(255,255,255,0.06)';
      ctx.fillRect(sx, y0, w, 2);
    }
  }
  const img = ctx.getImageData(0, 0, size, size);
  speckle(img.data, size, rng, 8);
  for (let i = 0; i < size * size; i += 1) {
    const luma = (img.data[i * 4] + img.data[i * 4 + 1] + img.data[i * 4 + 2]) / 3;
    img.data[i * 4 + 3] = 30 + luma * 0.85;
  }
  ctx.putImageData(img, 0, 0);
  return { albedo: toTexture(canvas) };
}

/** Oak planks for gates/portcullis beams. Tile ~1.6 m, vertical grain. */
export function paintWood(seed = 29): PaintedTexture {
  const size = 512;
  const { canvas, ctx } = makeCanvas(size);
  const rng = mulberry32(seed);
  ctx.fillStyle = '#6d4f30';
  ctx.fillRect(0, 0, size, size);
  const planks = 6;
  const pw = size / planks;
  for (let p = 0; p < planks; p += 1) {
    const x0 = p * pw;
    const shade = 0.82 + rng() * 0.36;
    ctx.fillStyle = `rgb(${Math.round(109 * shade)},${Math.round(79 * shade)},${Math.round(48 * shade)})`;
    ctx.fillRect(x0 + 2, 0, pw - 4, size);
    // Grain lines.
    for (let i = 0; i < 26; i += 1) {
      const gx = x0 + 4 + rng() * (pw - 8);
      ctx.strokeStyle = `rgba(58,40,22,${0.12 + rng() * 0.2})`;
      ctx.lineWidth = 0.7 + rng() * 1.4;
      ctx.beginPath();
      ctx.moveTo(gx, 0);
      for (let y = 0; y <= size; y += 16) {
        ctx.lineTo(gx + Math.sin(y * 0.02 + gx) * 2.4, y);
      }
      ctx.stroke();
    }
    // Knot.
    if (rng() > 0.55) {
      const kx = x0 + pw * 0.5;
      const ky = rng() * size;
      const kg = ctx.createRadialGradient(kx, ky, 1, kx, ky, pw * 0.28);
      kg.addColorStop(0, 'rgba(40,26,12,0.9)');
      kg.addColorStop(1, 'rgba(40,26,12,0)');
      ctx.fillStyle = kg;
      ctx.fillRect(kx - pw * 0.3, ky - pw * 0.3, pw * 0.6, pw * 0.6);
    }
  }
  const img = ctx.getImageData(0, 0, size, size);
  speckle(img.data, size, rng, 9);
  for (let i = 0; i < size * size; i += 1) {
    const luma = (img.data[i * 4] + img.data[i * 4 + 1] + img.data[i * 4 + 2]) / 3;
    img.data[i * 4 + 3] = 40 + luma * 0.8;
  }
  ctx.putImageData(img, 0, 0);
  return { albedo: toTexture(canvas) };
}


